import getAppCustomEnv from "@/utilities/get-app-custom-env";
import fetchIntercept from "fetch-intercept";
import LocalStoreManager from "@/utilities/local-store-manager";
import vuexStore from "@/store";
import moment from "moment-timezone";
import { includes } from "lodash";

const domain = window.location.hostname;
const isSleekDomain = includes(SLEEK_DOMAINS, domain);
import { Constants } from "customer-sdk/constants";

const { SLEEK_DOMAINS } = Constants.SLEEK_DOMAINS;
const HTTP_STATUS_CODES = Constants.HTTP_STATUS_CODES;

const getAppOrigin = () => {
  if (localStorage.getItem("isSUT") == "true") {
    return "sut";
  } else {
    return isSleekDomain ? "customer" : "mscomsec";
  }
};

let buildAuthenticatedJsonForPOSTRequest = (data, config = {}) => {
  const localStore = new LocalStoreManager();
  const sbaToken = localStore.getBusinessAccountToken();
  const authToken = localStore.getToken();
  if (!authToken) {
    throw new Error("Auth token is required.");
  }
  return {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: authToken,
      "X-SBA-Token": sbaToken,
      "Time-Zone": moment.tz.guess(),
      "app-origin": getAppOrigin(),
      ...config.headers,
    },
    body: JSON.stringify(data),
  };
};

let buildAuthenticatedJsonForPUTRequest = (params, config = {}) => {
  const localStore = new LocalStoreManager();
  const sbaToken = localStore.getBusinessAccountToken();
  const authToken = localStore.getToken();
  if (!authToken) {
    throw new Error("Auth token is required.");
  }
  return {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: authToken,
      "X-SBA-Token": sbaToken,
      "Time-Zone": moment.tz.guess(),
      "app-origin": getAppOrigin(),
      ...config.headers,
    },
    body: JSON.stringify(params),
  };
};

let buildAuthenticatedJsonForGETRequest = () => {
  const localStore = new LocalStoreManager();
  const sbaToken = localStore.getBusinessAccountToken();
  const authToken = localStore.getToken();
  if (!authToken) {
    throw new Error("Auth token is required.");
  }
  return {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: authToken,
      "X-SBA-Token": sbaToken,
      "Time-Zone": moment.tz.guess(),
      "app-origin": getAppOrigin(),
    },
  };
};

let buildAuthenticatedJsonForFileGETRequest = () => {
  const localStore = new LocalStoreManager();
  const sbaToken = localStore.getBusinessAccountToken();
  const authToken = localStore.getToken();
  if (!authToken) {
    throw new Error("Auth token is required.");
  }
  return {
    method: "GET",
    headers: {
      Accept: "*/*",
      Authorization: authToken,
      "X-SBA-Token": sbaToken,
      "Time-Zone": moment.tz.guess(),
      "app-origin": getAppOrigin(),
    },
  };
};

let buildAuthenticatedJsonForDELETERequest = (params = {}, config = {}) => {
  const localStore = new LocalStoreManager();
  const sbaToken = localStore.getBusinessAccountToken();
  const authToken = localStore.getToken();
  if (!authToken) {
    throw new Error("Auth token is required.");
  }
  return {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: authToken,
      "X-SBA-Token": sbaToken,
      "Time-Zone": moment.tz.guess(),
      "app-origin": getAppOrigin(),
      ...config.headers,
    },
    body: JSON.stringify(params),
  };
};

const authEnforcer = async (response) => {
  const localStore = new LocalStoreManager();
  const { status, headers } = response;
  if (status === HTTP_STATUS_CODES.HTTP_UNAUTHORIZED) {
    localStore.logout();
    const currentLocation = window.location.href;
    window.location.href = `/?redirect=${currentLocation}`;
    return;
  }

  if (status === HTTP_STATUS_CODES.HTTP_FORBIDDEN) {
    vuexStore.commit("businessAccountModule/showOTPForm", true);
    return;
  }

  const newToken = headers.get("x-sba-token");
  if (newToken) {
    localStore.setBusinessAccountToken(newToken);
  }
};

export default class BusinessAccountProxy {
  /**
   * @async
   * @function getSleekBankBackendUrl
   * @description Method used for retrieving the backend URL
   */
  async getBaseUrl() {
    let appCustomEnv = await getAppCustomEnv();
    if (!appCustomEnv.VUE_APP_SLEEK_BANK_BACKEND_URL) {
      throw new Error("SBA URL not found.");
    }
    return appCustomEnv.VUE_APP_SLEEK_BANK_BACKEND_URL;
  }

  /**
   * @async
   * @function postAuthenticatedData
   * @description Method used for posting data to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async postAuthenticatedData(url, data = {}, config = {}) {
    let requestOptions = buildAuthenticatedJsonForPOSTRequest(data, config);
    const baseUrl = await this.getBaseUrl();
    let response = await fetch(`${baseUrl}${url}`, requestOptions);
    authEnforcer(response);

    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    if (response.status === HTTP_STATUS_CODES.HTTP_ERROR_CODE) {
      let errorResponse = await response.json();

      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }

      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }

      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    } else if (contentType && contentType.indexOf("application/zip") !== -1) {
      return response;
    }

    return {};
  }

  /**
   * @async
   * @function putAuthenticatedData
   * @description Method used for PUT request to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async putAuthenticatedData(url, data, config = {}) {
    let requestOptions = buildAuthenticatedJsonForPUTRequest(data, config);
    const baseUrl = await this.getBaseUrl();
    let response = await fetch(`${baseUrl}${url}`, requestOptions);
    authEnforcer(response);
    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    if (response.status === HTTP_STATUS_CODES.HTTP_ERROR_CODE) {
      let errorResponse = await response.json();
      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }

  /**
   * @async
   * @function getAuthenticatedData
   * @description Method used for GET requests to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async getAuthenticatedData(url) {
    let requestOptions = buildAuthenticatedJsonForGETRequest();
    const baseUrl = await this.getBaseUrl();
    let response = await fetch(`${baseUrl}${url}`, requestOptions);
    authEnforcer(response);
    return await response.json();
  }

  /**
   * @async
   * @function getAuthenticatedFileData
   * @description Method used for GET requests for files.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   */
  async getAuthenticatedFileData(url) {
    let requestOptions = buildAuthenticatedJsonForFileGETRequest();
    const baseUrl = await this.getBaseUrl();
    let response = await fetch(`${baseUrl}${url}`, requestOptions);
    authEnforcer(response);
    return response;
  }

  /**
   * @async
   * @function deleteAuthenticatedData
   * @description Method used for GET requests to a target endpoint with authorization header.
   * @param {*} url - The URL of the target endpoint.
   * @param {*} token - Authorization token of current user.
   * @param {*} data - Object parameters to be forwarded.
   */
  async deleteAuthenticatedData(url, data = {}, config = {}) {
    let requestOptions = buildAuthenticatedJsonForDELETERequest(data, config);
    const baseUrl = await this.getBaseUrl();
    let response = await fetch(`${baseUrl}${url}`, requestOptions);
    authEnforcer(response);
    if (response.status === HTTP_STATUS_CODES.HTTP_NOT_FOUND_CODE) {
      throw Error([{ error_code: "page_not_found", message: "Page not found" }]);
    }

    if (response.status >= 400) {
      let errorResponse = await response.json();
      if (errorResponse.message) {
        throw Error(errorResponse.message);
      }
      if (errorResponse.length > 0) {
        throw Error(errorResponse[0]);
      }
      throw Error();
    }

    let contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return await response.json();
    }
    return {};
  }
}

fetchIntercept.register({
  response: function (response) {
    // Modify the reponse object
    if (response.status == 429) {
      alert("You have too many requests, Please try again later.");
    }
    return response;
  },
});
